/**
 * Step-by-step traces of six classic comparison sorts.
 *
 * Each algorithm is written the textbook way and records what it does as it does it:
 * every comparison, swap and array write becomes a `Step` carrying a snapshot of the
 * array, so the visualizer replays the real algorithm rather than an animation of one.
 * Positions in `marks`, `settled` and `range` are 0-based; positions in message
 * parameters are 1-based because they are meant to be read.
 *
 * Counting rules, stated on the page too: a comparison is one test of two values; a
 * swap exchanges two array elements (and is two writes); a write is any assignment to
 * the array. Insertion sort shifts elements instead of swapping, and merge sort writes
 * merged values back from a temporary copy, so those two show writes and no swaps.
 */

export type AlgorithmId =
  | "bubble"
  | "insertion"
  | "selection"
  | "merge"
  | "quick"
  | "heap";

export const ALGORITHM_IDS: readonly AlgorithmId[] = [
  "bubble",
  "insertion",
  "selection",
  "merge",
  "quick",
  "heap",
];

export type Mark = "compare" | "swap" | "write" | "pivot";

export type MessageKey =
  | "start"
  | "compare"
  | "newMin"
  | "swap"
  | "write"
  | "settle"
  | "pivot"
  | "merge"
  | "heapBuild"
  | "heapExtract"
  | "noSwapsDone"
  | "done";

export type Relation = "gt" | "eq" | "lt";

export interface Message {
  key: MessageKey;
  /** Values and 1-based positions to fill into the sentence. */
  params: Record<string, number>;
  relation?: Relation;
}

export interface Aux {
  /** 0-based position in the main array where the copy begins. */
  offset: number;
  values: number[];
  /** Marks keyed by index into `values`. */
  marks: Record<number, Mark>;
}

export interface Step {
  array: number[];
  marks: Record<number, Mark>;
  /** Positions that hold their final value. */
  settled: number[];
  /** The part of the array the algorithm is working on, inclusive; null for all of it. */
  range: [number, number] | null;
  aux: Aux | null;
  comparisons: number;
  swaps: number;
  writes: number;
  message: Message;
}

export interface Counts {
  comparisons: number;
  swaps: number;
  writes: number;
}

/** The largest input the visualizer accepts. */
export const MAX_ITEMS = 40;
export const MIN_ITEMS = 2;

interface Tracer {
  a: number[];
  steps: Step[];
  settled: Set<number>;
  counts: Counts;
  range: [number, number] | null;
}

function createTracer(input: readonly number[]): Tracer {
  const tracer: Tracer = {
    a: [...input],
    steps: [],
    settled: new Set<number>(),
    counts: { comparisons: 0, swaps: 0, writes: 0 },
    range: null,
  };
  emit(tracer, {}, { key: "start", params: { count: input.length } });
  return tracer;
}

function emit(
  tracer: Tracer,
  marks: Record<number, Mark>,
  message: Message,
  aux: Aux | null = null
): void {
  tracer.steps.push({
    array: [...tracer.a],
    marks,
    settled: [...tracer.settled].sort((x, y) => x - y),
    range: tracer.range,
    aux,
    comparisons: tracer.counts.comparisons,
    swaps: tracer.counts.swaps,
    writes: tracer.counts.writes,
    message,
  });
}

function relationOf(left: number, right: number): Relation {
  if (left > right) {
    return "gt";
  }
  return left === right ? "eq" : "lt";
}

/** Record one comparison of two array positions (or of a held value, with index null). */
function compare(
  tracer: Tracer,
  left: { value: number; at: number | null },
  right: { value: number; at: number | null },
  aux: Aux | null = null
): Relation {
  tracer.counts.comparisons += 1;
  const marks: Record<number, Mark> = {};
  for (const side of [left, right]) {
    if (side.at !== null) {
      marks[side.at] = "compare";
    }
  }
  const relation = relationOf(left.value, right.value);
  emit(
    tracer,
    marks,
    {
      key: "compare",
      params: {
        left: left.value,
        right: right.value,
        at: (left.at ?? -1) + 1,
        at2: (right.at ?? -1) + 1,
      },
      relation,
    },
    aux
  );
  return relation;
}

function swap(tracer: Tracer, i: number, j: number): void {
  if (i === j) {
    return;
  }
  const a = tracer.a[i] as number;
  const b = tracer.a[j] as number;
  tracer.a[i] = b;
  tracer.a[j] = a;
  tracer.counts.swaps += 1;
  tracer.counts.writes += 2;
  emit(
    tracer,
    { [i]: "swap", [j]: "swap" },
    { key: "swap", params: { a, b, at: i + 1, at2: j + 1 } }
  );
}

function write(
  tracer: Tracer,
  i: number,
  value: number,
  aux: Aux | null = null
) {
  tracer.a[i] = value;
  tracer.counts.writes += 1;
  emit(
    tracer,
    { [i]: "write" },
    { key: "write", params: { value, at: i + 1 } },
    aux
  );
}

function settle(tracer: Tracer, i: number): void {
  tracer.settled.add(i);
  emit(
    tracer,
    {},
    { key: "settle", params: { value: tracer.a[i] as number, at: i + 1 } }
  );
}

function finish(tracer: Tracer): Step[] {
  tracer.range = null;
  for (let i = 0; i < tracer.a.length; i++) {
    tracer.settled.add(i);
  }
  emit(tracer, {}, { key: "done", params: { count: tracer.a.length } });
  return tracer.steps;
}

// ---------------------------------------------------------------------------
// The algorithms
// ---------------------------------------------------------------------------

function bubble(tracer: Tracer): void {
  const n = tracer.a.length;
  for (let pass = 0; pass < n - 1; pass++) {
    let swapped = false;
    const last = n - 1 - pass;
    tracer.range = [0, last];
    for (let j = 0; j < last; j++) {
      const left = tracer.a[j] as number;
      const right = tracer.a[j + 1] as number;
      if (
        compare(tracer, { value: left, at: j }, { value: right, at: j + 1 }) ===
        "gt"
      ) {
        swap(tracer, j, j + 1);
        swapped = true;
      }
    }
    if (!swapped) {
      emit(tracer, {}, { key: "noSwapsDone", params: { pass: pass + 1 } });
      return;
    }
    settle(tracer, last);
  }
}

function insertion(tracer: Tracer): void {
  const n = tracer.a.length;
  for (let i = 1; i < n; i++) {
    tracer.range = [0, i];
    const key = tracer.a[i] as number;
    let j = i - 1;
    let moved = false;
    while (j >= 0) {
      const current = tracer.a[j] as number;
      if (
        compare(tracer, { value: key, at: null }, { value: current, at: j }) !==
        "lt"
      ) {
        // Not smaller than a[j]: key belongs just after it. (An equal value stays put: stable.)
        break;
      }
      write(tracer, j + 1, current);
      moved = true;
      j -= 1;
    }
    if (moved) {
      write(tracer, j + 1, key);
    }
  }
}

function selection(tracer: Tracer): void {
  const n = tracer.a.length;
  for (let i = 0; i < n - 1; i++) {
    tracer.range = [i, n - 1];
    let min = i;
    for (let j = i + 1; j < n; j++) {
      const candidate = tracer.a[j] as number;
      const smallest = tracer.a[min] as number;
      if (
        compare(
          tracer,
          { value: candidate, at: j },
          { value: smallest, at: min }
        ) === "lt"
      ) {
        min = j;
        emit(
          tracer,
          { [j]: "pivot" },
          { key: "newMin", params: { value: candidate, at: j + 1 } }
        );
      }
    }
    swap(tracer, i, min);
    settle(tracer, i);
  }
}

function merge(tracer: Tracer, lo: number, mid: number, hi: number): void {
  tracer.range = [lo, hi];
  const copy = tracer.a.slice(lo, hi + 1);
  emit(
    tracer,
    {},
    { key: "merge", params: { lo: lo + 1, mid: mid + 1, hi: hi + 1 } },
    { offset: lo, values: copy, marks: {} }
  );
  let left = lo;
  let right = mid + 1;
  for (let k = lo; k <= hi; k++) {
    const auxAt = (index: number) => ({
      offset: lo,
      values: copy,
      marks: { [index - lo]: "compare" as Mark },
    });
    let takeLeft: boolean;
    if (left > mid) {
      takeLeft = false;
    } else if (right > hi) {
      takeLeft = true;
    } else {
      const l = copy[left - lo] as number;
      const r = copy[right - lo] as number;
      const aux: Aux = {
        offset: lo,
        values: copy,
        marks: { [left - lo]: "compare", [right - lo]: "compare" },
      };
      takeLeft =
        compare(tracer, { value: l, at: null }, { value: r, at: null }, aux) !==
        "gt";
    }
    const source = takeLeft ? left : right;
    write(tracer, k, copy[source - lo] as number, auxAt(source));
    if (takeLeft) {
      left += 1;
    } else {
      right += 1;
    }
  }
}

function mergeSort(tracer: Tracer, lo: number, hi: number): void {
  if (lo >= hi) {
    return;
  }
  const mid = Math.floor((lo + hi) / 2);
  mergeSort(tracer, lo, mid);
  mergeSort(tracer, mid + 1, hi);
  merge(tracer, lo, mid, hi);
}

function quickSort(tracer: Tracer, lo: number, hi: number): void {
  if (lo > hi) {
    return;
  }
  if (lo === hi) {
    tracer.range = [lo, hi];
    settle(tracer, lo);
    return;
  }
  tracer.range = [lo, hi];
  const pivot = tracer.a[hi] as number;
  emit(
    tracer,
    { [hi]: "pivot" },
    {
      key: "pivot",
      params: { value: pivot, at: hi + 1, lo: lo + 1, hi: hi + 1 },
    }
  );
  let store = lo;
  for (let j = lo; j < hi; j++) {
    const value = tracer.a[j] as number;
    if (compare(tracer, { value, at: j }, { value: pivot, at: hi }) === "lt") {
      swap(tracer, store, j);
      store += 1;
    }
  }
  swap(tracer, store, hi);
  settle(tracer, store);
  quickSort(tracer, lo, store - 1);
  quickSort(tracer, store + 1, hi);
}

function siftDown(tracer: Tracer, start: number, end: number): void {
  let root = start;
  while (2 * root + 1 < end) {
    let child = 2 * root + 1;
    if (child + 1 < end) {
      const l = tracer.a[child] as number;
      const r = tracer.a[child + 1] as number;
      if (
        compare(
          tracer,
          { value: r, at: child + 1 },
          { value: l, at: child }
        ) === "gt"
      ) {
        child += 1;
      }
    }
    const parent = tracer.a[root] as number;
    const bigger = tracer.a[child] as number;
    if (
      compare(
        tracer,
        { value: bigger, at: child },
        { value: parent, at: root }
      ) !== "gt"
    ) {
      return;
    }
    swap(tracer, root, child);
    root = child;
  }
}

function heapSort(tracer: Tracer): void {
  const n = tracer.a.length;
  tracer.range = [0, n - 1];
  emit(tracer, {}, { key: "heapBuild", params: { count: n } });
  for (let start = Math.floor(n / 2) - 1; start >= 0; start--) {
    siftDown(tracer, start, n);
  }
  for (let end = n - 1; end > 0; end--) {
    tracer.range = [0, end];
    emit(
      tracer,
      { 0: "pivot" },
      {
        key: "heapExtract",
        params: { value: tracer.a[0] as number, at: end + 1 },
      }
    );
    swap(tracer, 0, end);
    settle(tracer, end);
    siftDown(tracer, 0, end);
  }
}

/** Trace one algorithm on a copy of the input. The first step is the unsorted start. */
export function traceSort(id: AlgorithmId, input: readonly number[]): Step[] {
  const tracer = createTracer(input);
  const last = input.length - 1;
  switch (id) {
    case "bubble":
      bubble(tracer);
      break;
    case "insertion":
      insertion(tracer);
      break;
    case "selection":
      selection(tracer);
      break;
    case "merge":
      mergeSort(tracer, 0, last);
      break;
    case "quick":
      quickSort(tracer, 0, last);
      break;
    default:
      heapSort(tracer);
  }
  return finish(tracer);
}

export function countsOf(steps: readonly Step[]): Counts {
  const last = steps.at(-1);
  return last
    ? { comparisons: last.comparisons, swaps: last.swaps, writes: last.writes }
    : { comparisons: 0, swaps: 0, writes: 0 };
}

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export type Pattern =
  | "random"
  | "nearlySorted"
  | "reversed"
  | "sorted"
  | "fewUnique";

export const PATTERNS: readonly Pattern[] = [
  "random",
  "nearlySorted",
  "reversed",
  "sorted",
  "fewUnique",
];

// biome-ignore-start lint/suspicious/noBitwiseOperators: a 32-bit generator is bit mixing by definition

/** Mulberry32: a small, well-mixed 32-bit generator. The same seed gives the same input. */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d_2b_79_f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the generator

function shuffled(values: number[], random: () => number): number[] {
  const out = [...values];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j] as number, out[i] as number];
  }
  return out;
}

/** A test input of `count` values in the given pattern, reproducible from `seed`. */
export function generateInput(
  pattern: Pattern,
  count: number,
  seed: number
): number[] {
  const random = seededRandom(seed);
  const ordered = Array.from({ length: count }, (_, i) => i + 1);
  switch (pattern) {
    case "sorted":
      return ordered;
    case "reversed":
      return ordered.reverse();
    case "nearlySorted": {
      const out = [...ordered];
      const swaps = Math.max(1, Math.round(count / 8));
      for (let s = 0; s < swaps; s++) {
        const i = Math.floor(random() * (count - 1));
        [out[i], out[i + 1]] = [out[i + 1] as number, out[i] as number];
      }
      return out;
    }
    case "fewUnique":
      return ordered.map(() => 1 + Math.floor(random() * 4));
    default:
      return shuffled(ordered, random);
  }
}
